import useUrlState from '@ahooksjs/use-url-state'
import { useRequest } from 'ahooks'
import { Select, Spin } from 'antd'
import { useAtom } from 'jotai'
import { groupBy } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { DepartmentTree, useDepartmentTree } from '@/components/department-tree.component'
import { Department, departmentsAtom } from '@/modules/department'
import { currentOKRCycleAtom, okrCyclesAtom } from '@/modules/okr'
import { userInfoAtom } from '@/modules/user'
import { ga, traceEvent } from '@/utils/ga'

import { OKRMap } from './okr-map.component'

export const OKRContainer = () => {
  const { t } = useTranslation()
  const [okrCycles] = useAtom(okrCyclesAtom)
  const [currentOKRCycle] = useAtom(currentOKRCycleAtom)
  const [userInfo] = useAtom(userInfoAtom)

  const [state, setState] = useUrlState(
    { departmentId: userInfo!.departmentIds[0], cycleId: null },
    {
      navigateMode: 'replace',
    }
  )
  const departmentId = Number(state.departmentId)

  React.useEffect(() => {
    ga('event', traceEvent.navigation.events.show_navigation, {
      event_category: traceEvent.navigation.event_categories.ef_pmo,
      event_label: traceEvent.navigation.event_labels.okrProject,
      action: traceEvent.navigation.action,
    })
  }, [])

  React.useEffect(() => {
    if (currentOKRCycle) {
      setState({ ...state, cycleId: currentOKRCycle.cycleId })
    }
  }, [currentOKRCycle])

  const [departments] = useAtom(departmentsAtom)

  const { treeData, parentIds } = useDepartmentTree({ currentDepartmentId: departmentId })

  const { data } = useRequest(
    {
      url: '/api/okr/getObjectives',
      params: {
        cycleId: state.cycleId,
      },
    },
    {
      ready: !!state.cycleId,
      refreshDeps: [state.cycleId],
    }
  )

  if (!data) {
    return (
      <div className='flex h-screen w-full items-center justify-center'>
        <Spin size='large' />
      </div>
    )
  }

  const parentPath = parentIds.slice().reverse().join('.')
  const tree = departments.filter((d) => parentIds.includes(d.id) || d.path.startsWith(parentPath))
  const okrMap = groupBy(data.objectives, 'departmentId')
  const ds = tree.map((d) => ({
    ...d,
    parentId: d.parentId,
    children: [] as any[],
    objectives: okrMap[d.id] || [],
  }))
  ds.forEach((d) => {
    d.children = ds.filter((dd) => dd.parentId === d.id)
  })
  const root = ds.find((d) => d.id === parentIds[parentIds.length - 1])!

  return (
    <div className='flex'>
      <DepartmentTree
        treeData={treeData}
        selectedKeys={parentIds}
        onChangeKey={(key) => setState({ ...state, departmentId: key })}
      />
      <OKRMap root={root as Department} objectives={data.objectives} departmentId={departmentId} />
      <div className='top-12px right-12px fixed'>
        {t('change quarter')}:
        <Select
          options={okrCycles.map((c) => ({ value: c.cycleId, label: c.name }))}
          value={Number(state.cycleId)}
          onChange={(cycleId) => setState({ ...state, cycleId })}
        />
      </div>
    </div>
  )
}
